import type { DateRangePickerProps } from "@amzn/awsui-components-console";

import type { SearchDateRange } from "./types";

const ISO_DATE_LENGTH = 10;

/** The local calendar date of an instant, as `YYYY-MM-DD`. */
export function toIsoCalendarDate(value: Date): string {
  const month = `${value.getMonth() + 1}`.padStart(2, "0");
  const day = `${value.getDate()}`.padStart(2, "0");
  return `${value.getFullYear()}-${month}-${day}`;
}

/**
 * Resolved in the viewer's zone, because the picker's absolute selections are
 * calendar dates chosen in that zone and the two have to be comparable.
 * Sub-day units resolve to today, since the bounds are whole days.
 */
export function startOfRelativeRange(
  amount: number,
  unit: DateRangePickerProps.RelativeValue["unit"]
): Date {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const day = now.getDate();

  /**
   * Clamps to the target month's last day. Without it, month and year arithmetic
   * overflows into the following month whenever today's day-of-month does not
   * exist in the target: six months before 31 August is 31 February, which
   * normalizes to 2 or 3 March, so the search would silently start days late and
   * miss claims at the boundary. A year before 29 February has the same problem.
   */
  const clampedToMonth = (targetYear: number, targetMonth: number): Date => {
    const lastDayOfTargetMonth = new Date(targetYear, targetMonth + 1, 0).getDate();
    return new Date(targetYear, targetMonth, Math.min(day, lastDayOfTargetMonth));
  };

  switch (unit) {
    case "year":
      return clampedToMonth(year - amount, month);
    case "month":
      return clampedToMonth(year, month - amount);
    // Day counts need no clamping: the Date constructor rolls a day offset back
    // across month and year boundaries correctly.
    case "week":
      return new Date(year, month, day - amount * 7);
    case "day":
      return new Date(year, month, day - amount);
    default:
      return now;
  }
}

/**
 * The picker value as the two absolute calendar dates the dispatcher expects. A
 * relative selection is resolved here, at the moment it is made, so nothing
 * downstream has to interpret "past 6 months" against a later clock.
 */
export function toSearchDateRange(
  value: DateRangePickerProps.Value | null
): SearchDateRange | null {
  if (!value) {
    return null;
  }
  if (value.type === "absolute") {
    return {
      start: value.startDate.slice(0, ISO_DATE_LENGTH),
      end: value.endDate.slice(0, ISO_DATE_LENGTH),
    };
  }
  if (!value.amount || !value.unit) {
    return null;
  }
  return {
    start: toIsoCalendarDate(startOfRelativeRange(value.amount, value.unit)),
    end: toIsoCalendarDate(new Date()),
  };
}

/**
 * A relative selection displays as the absolute range it resolved to, because
 * the resolved range is what was searched and there is one stored form of it.
 */
export function toPickerValue(range: SearchDateRange | null): DateRangePickerProps.Value | null {
  return range === null ? null : { type: "absolute", startDate: range.start, endDate: range.end };
}
